import { RefundDestinationType, RefundPaymentRail } from 'src/constants/payments-ops/enums'
import type { ExecuteRefundBody, RefundRecipientAddress } from 'src/interfaces/refund'
import { maskAccountNumber } from './maskAccountNumber'
import type { ExecuteRefundFormValues } from './refundExecute.types'

/** The backend requires (and the form shows) the recipient address only for this combination. */
export const isRefundAddressRequired = (
  destinationType: RefundDestinationType,
  paymentRail: RefundPaymentRail,
): boolean => {
  return (
    destinationType === RefundDestinationType.CustomerAccount &&
    paymentRail === RefundPaymentRail.Rtp
  )
}

const buildRecipientAddress = (values: ExecuteRefundFormValues): RefundRecipientAddress => {
  const addressLine2 = values.addressLine2.trim()

  return {
    addressLine1: values.addressLine1.trim(),
    ...(addressLine2 === '' ? {} : { addressLine2 }),
    city: values.city.trim(),
    state: values.state.trim(),
    zipCode: values.zipCode.trim(),
  }
}

export const buildExecuteRefundBody = (values: ExecuteRefundFormValues): ExecuteRefundBody => {
  if (values.destinationType === RefundDestinationType.GigsafeAccount) {
    return {
      destinationType: RefundDestinationType.GigsafeAccount,
      paymentRail: values.paymentRail,
    }
  }

  const bank = {
    destinationType: RefundDestinationType.CustomerAccount as const,
    recipientName: values.recipientName.trim(),
    routingNumber: values.routingNumber.trim(),
    accountNumber: values.accountNumber.trim(),
  }

  if (values.paymentRail === RefundPaymentRail.Rtp) {
    return { ...bank, paymentRail: RefundPaymentRail.Rtp, ...buildRecipientAddress(values) }
  }

  return { ...bank, paymentRail: RefundPaymentRail.Ach }
}

/** One-line address for the recap, or `null` when no address is sent. */
export const formatRefundRecipientAddress = (values: ExecuteRefundFormValues): string | null => {
  if (!isRefundAddressRequired(values.destinationType, values.paymentRail)) {
    return null
  }

  const address = buildRecipientAddress(values)
  const streetParts = [address.addressLine1, address.addressLine2 ?? ''].filter((part) => {
    return part !== ''
  })

  return [...streetParts, address.city, `${address.state} ${address.zipCode}`].join(', ')
}

/** The recap sentence; the rail and address are shown as their own rows under it. */
export const buildRefundRecapDescription = (
  values: ExecuteRefundFormValues,
  amount: string,
): string => {
  const consequence = 'This sends money and cannot be undone.'

  if (values.destinationType === RefundDestinationType.CustomerAccount) {
    return `Send ${amount} to ${values.recipientName.trim()}, routing ${maskAccountNumber(
      values.routingNumber,
    )}, account ${maskAccountNumber(values.accountNumber)}? ${consequence}`
  }

  return `Send ${amount} to GigSafe's settlement account? ${consequence}`
}
